import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Printer, User, Calendar, Package, Mail, Truck, CheckCircle2, XCircle, Clock, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-800", icon: Package },
  shipped: { label: "Shipped", color: "bg-purple-100 text-purple-800", icon: Truck },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle }
};

export function TonerManagementAdmin() {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [trackingCode, setTrackingCode] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${getBaseURL()}/api/toner/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort by date (newest first)
      const sorted = response.data.sort((a, b) => {
        const dateA = new Date(a.submission_date);
        const dateB = new Date(b.submission_date);
        return dateB - dateA;
      });
      
      setSubmissions(sorted);
    } catch (error) {
      console.error("Load submissions error:", error);
      toast.error("Failed to load toner orders");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleOpenStatusDialog = (submission) => {
    setSelectedSubmission(submission);
    setNewStatus("");
    setTrackingCode("");
    setShowStatusDialog(true);
  };

  const handleOpenVoidDialog = (submission) => {
    setSelectedSubmission(submission);
    setShowVoidDialog(true);
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailsDialog(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    if (newStatus === "shipped" && !trackingCode.trim()) {
      toast.error("Please enter a tracking code for shipped orders");
      return;
    }

    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem("auth_token");
      
      await axios.put(
        `${getBaseURL()}/api/toner/submissions/${selectedSubmission.id}/status`,
        { 
          status: newStatus,
          tracking_code: newStatus === "shipped" ? trackingCode : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Order status updated to ${newStatus}`);
      setShowStatusDialog(false);
      loadSubmissions();
    } catch (error) {
      console.error("Status update error:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleVoidOrder = async () => {
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem("auth_token");
      
      await axios.put(
        `${getBaseURL()}/api/toner/submissions/${selectedSubmission.id}/status`,
        { status: "cancelled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Order has been voided");
      setShowVoidDialog(false);
      loadSubmissions();
    } catch (error) {
      console.error("Void order error:", error);
      toast.error("Failed to void order");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getOrderSummary = (printers) => {
    const items = [];
    const compat = (t) => t === "compatible" ? " (Compatible)" : "";
    printers.forEach(p => {
      if (p.asset_type === "color_printer") {
        if (p.order_black > 0) items.push(`Black: ${p.order_black}${compat(p.black_type)}`);
        if (p.order_yellow > 0) items.push(`Yellow: ${p.order_yellow}${compat(p.yellow_type)}`);
        if (p.order_cyan > 0) items.push(`Cyan: ${p.order_cyan}${compat(p.cyan_type)}`);
        if (p.order_magenta > 0) items.push(`Magenta: ${p.order_magenta}${compat(p.magenta_type)}`);
      } else {
        if (p.toners_required > 0) items.push(`Toners: ${p.toners_required}${compat(p.toner_type)}`);
      }
      if (p.drum_required > 0) items.push(`Drums: ${p.drum_required}${compat(p.drum_type)}`);
    });
    return items.length > 0 ? items.join(", ") : "No items";
  };

  const activeOrders = submissions.filter(s => s.status !== "cancelled" && s.status !== "delivered");
  const historyOrders = submissions.filter(s => s.status === "cancelled" || s.status === "delivered");

  const renderOrderCard = (submission, showVoidButton = true) => {
    const StatusIcon = STATUS_CONFIG[submission.status]?.icon || Clock;
    const isCancelled = submission.status === "cancelled";

    return (
      <Card 
        key={submission.id} 
        className={`relative overflow-hidden ${isCancelled ? 'opacity-75' : ''}`}
        data-testid={`order-card-${submission.id}`}
      >
        {isCancelled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <span className="text-6xl font-bold text-red-500 opacity-30 rotate-[-15deg]">
              CANCELLED
            </span>
          </div>
        )}
        
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{submission.customer_name}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Calendar className="h-3 w-3" />
                {formatDate(submission.submission_date)}
              </CardDescription>
            </div>
            <Badge className={STATUS_CONFIG[submission.status]?.color || "bg-gray-100"}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {STATUS_CONFIG[submission.status]?.label || submission.status}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-3">
            {/* Order Summary */}
            <div className="text-sm text-gray-600">
              <span className="font-medium">Order: </span>
              {getOrderSummary(submission.printers)}
            </div>

            {/* Tracking Code */}
            {submission.tracking_code && (
              <div className="text-sm bg-purple-50 p-2 rounded flex items-center gap-2">
                <Truck className="h-4 w-4 text-purple-600" />
                <span className="font-medium">Tracking:</span> {submission.tracking_code}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleViewDetails(submission)}
                data-testid={`view-details-${submission.id}`}
              >
                <Eye className="h-4 w-4 mr-1" />
                View Details
              </Button>
              
              {!isCancelled && submission.status !== "delivered" && (
                <>
                  <Button 
                    size="sm" 
                    onClick={() => handleOpenStatusDialog(submission)}
                    data-testid={`change-status-${submission.id}`}
                  >
                    Change Status
                  </Button>
                  
                  {showVoidButton && (
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleOpenVoidDialog(submission)}
                      data-testid={`void-order-${submission.id}`}
                    >
                      Void Order
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Toner Orders</h1>
          <p className="text-gray-600 mt-1">Manage customer toner and supplies orders</p>
        </div>
        <Button onClick={loadSubmissions} variant="outline" data-testid="refresh-orders">
          Refresh Orders
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No Orders Yet</h3>
            <p className="text-gray-500 mt-2">
              Toner orders from customers will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="active" data-testid="active-orders-tab">
              Active Orders ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-orders-tab">
              Order History ({historyOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {activeOrders.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No active orders
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {activeOrders.map(submission => renderOrderCard(submission))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            {historyOrders.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No order history
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {historyOrders.map(submission => renderOrderCard(submission, false))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Order Status</DialogTitle>
            <DialogDescription>
              Update the status for order from {selectedSubmission?.customer_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="processing">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Processing
                    </div>
                  </SelectItem>
                  <SelectItem value="shipped">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Shipped
                    </div>
                  </SelectItem>
                  <SelectItem value="delivered">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Delivered
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === "shipped" && (
              <div className="space-y-2">
                <Label htmlFor="tracking">Tracking Code *</Label>
                <Input
                  id="tracking"
                  value={trackingCode}
                  onChange={(e) => setTrackingCode(e.target.value)}
                  placeholder="Enter tracking code"
                  data-testid="tracking-code-input"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={updatingStatus}>
              {updatingStatus ? "Updating..." : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Order Confirmation Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Void Order
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to void this order? This action will notify the customer via email.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium">{selectedSubmission?.customer_name}</p>
              <p className="text-sm text-gray-600 mt-1">
                Order Date: {formatDate(selectedSubmission?.submission_date)}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleVoidOrder} 
              disabled={updatingStatus}
              data-testid="confirm-void-button"
            >
              {updatingStatus ? "Voiding..." : "Void Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Order from {selectedSubmission?.customer_name}
            </DialogDescription>
          </DialogHeader>

          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Order ID:</span>
                  <p className="font-mono text-xs">{selectedSubmission.id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p>{formatDate(selectedSubmission.submission_date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <Badge className={`mt-1 ${STATUS_CONFIG[selectedSubmission.status]?.color}`}>
                    {STATUS_CONFIG[selectedSubmission.status]?.label}
                  </Badge>
                </div>
                {selectedSubmission.tracking_code && (
                  <div>
                    <span className="text-gray-500">Tracking Code:</span>
                    <p className="font-medium">{selectedSubmission.tracking_code}</p>
                  </div>
                )}
              </div>

              {/* Printer Orders */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedSubmission.printers.map((printer, idx) => (
                    <div key={printer.printer_id || idx} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Printer className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{printer.printer_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {printer.asset_type === "color_printer" ? "Color" : "B&W"}
                          </Badge>
                        </div>
                        {printer.location && (
                          <span className="text-sm text-gray-500">{printer.location}</span>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        {printer.asset_type === "color_printer" ? (
                          <div className="flex flex-wrap gap-3">
                            {printer.order_black > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-black"></span>
                                Black: {printer.order_black}{printer.black_type === "compatible" ? " (Compatible)" : ""}
                              </span>
                            )}
                            {printer.order_yellow > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                                Yellow: {printer.order_yellow}{printer.yellow_type === "compatible" ? " (Compatible)" : ""}
                              </span>
                            )}
                            {printer.order_cyan > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-cyan-500"></span>
                                Cyan: {printer.order_cyan}{printer.cyan_type === "compatible" ? " (Compatible)" : ""}
                              </span>
                            )}
                            {printer.order_magenta > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-pink-500"></span>
                                Magenta: {printer.order_magenta}{printer.magenta_type === "compatible" ? " (Compatible)" : ""}
                              </span>
                            )}
                            {printer.drum_required > 0 && (
                              <span>Drums: {printer.drum_required}{printer.drum_type === "compatible" ? " (Compatible)" : ""}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex gap-4">
                            {printer.toners_required > 0 && (
                              <span>Toners: {printer.toners_required}{printer.toner_type === "compatible" ? " (Compatible)" : ""}</span>
                            )}
                            {printer.drum_required > 0 && (
                              <span>Drums: {printer.drum_required}{printer.drum_type === "compatible" ? " (Compatible)" : ""}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status History */}
              {selectedSubmission.status_history && selectedSubmission.status_history.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Status History</h4>
                  <div className="space-y-2">
                    {selectedSubmission.status_history.map((entry, idx) => (
                      <div key={`${entry.status}-${entry.timestamp || idx}`} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_CONFIG[entry.status]?.color || "bg-gray-100"}>
                            {STATUS_CONFIG[entry.status]?.label || entry.status}
                          </Badge>
                          {entry.tracking_code && (
                            <span className="text-gray-500">
                              Tracking: {entry.tracking_code}
                            </span>
                          )}
                        </div>
                        <span className="text-gray-500">
                          {formatDate(entry.timestamp)}
                          {entry.changed_by && ` by ${entry.changed_by}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TonerManagementAdmin;
